<template>

    <div class="segmented" :class="classes" role="radiogroup">
        <div class="segmented-thumb" :class="{ 'is-ready': thumbReady, 'is-visible': thumbWidth }" :style="thumbStyle" />
        <button
            v-for="item in items"
            :key="item.key"
            class="segmented-option"
            :class="{ active: isActive(item) }"
            type="button"
            role="radio"
            :aria-checked="isActive(item)"
            :disabled="disabled"
            @click="selectItem(item)">
            <i v-if="item.icon" :class="item.icon" aria-hidden="true" />
            {{ item.value }}
        </button>
    </div>

</template>

<style scoped lang="scss">

    .segmented {
        background: $color-bg;
        border: 1px solid $color-rule;
        border-radius: 5px;
        box-sizing: border-box;
        display: inline-flex;
        flex-shrink: 0;
        padding: 2px;
        position: relative;
        &.disabled {
            cursor: default;
            opacity: 0.7;
            pointer-events: none;
        }
        &.size-xs {
            height: 30px;
            .segmented-option {
                @include font-medium(10px);
                padding: 0 10px;
            }
        }
        &.size-sm {
            height: 40px;
            .segmented-option {
                @include font-medium(11px);
                padding: 0 15px;
            }
        }
    }

    .segmented-thumb {
        background: $color-primary;
        border-radius: 3px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        height: calc(100% - 4px);
        left: 0;
        pointer-events: none;
        position: absolute;
        top: 2px;
        visibility: hidden;
        &.is-ready {
            transition: transform .25s ease, width .25s ease;
        }
        &.is-visible {
            visibility: visible;
        }
    }

    .segmented-option {
        align-items: center;
        appearance: none;
        background: transparent;
        border: none;
        color: $color-text;
        cursor: pointer;
        display: flex;
        flex-shrink: 0;
        gap: 5px;
        justify-content: center;
        margin: 0;
        outline: none;
        overflow: hidden;
        position: relative;
        text-overflow: ellipsis;
        transition: color .25s ease;
        user-select: none;
        white-space: nowrap;
        z-index: 1;
        &.active {
            color: $color-white;
        }
    }

</style>

<script>

export default {

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            required: true,
        },
        model: {
            required: true,
        },
        size: {
            type: String,
            default: 'sm',
        },
    },

    data() {
        return {
            thumbLeft: 0,
            thumbReady: false,
            thumbWidth: 0,
        };
    },

    computed: {

        classes() {
            return {
                'disabled': this.disabled,
                'size-sm': this.size === 'sm',
                'size-xs': this.size === 'xs',
            };
        },

        selectedIndex() {
            const index = this.items.findIndex(item => item.key === this.model);
            return index === -1 ? 0 : index;
        },

        thumbStyle() {
            return {
                transform: `translateX(${this.thumbLeft}px)`,
                width: `${this.thumbWidth}px`,
            };
        },

    },

    mounted() {
        this.$nextTick(() => {
            this.updateThumb();
            if (typeof ResizeObserver === 'undefined') {
                return;
            }
            this.resizeObserver = new ResizeObserver(this.updateThumb);
            this.resizeObserver.observe(this.$el);
        });
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    watch: {

        items: {
            deep: true,
            handler() {
                this.$nextTick(this.updateThumb);
            },
        },

        model() {
            this.$nextTick(this.updateThumb);
        },

    },

    methods: {

        isActive(item) {
            return this.model === item.key;
        },

        selectItem(item) {
            if (this.disabled || this.isActive(item)) {
                return;
            }
            this.$emit('input', item.key);
        },

        updateThumb() {
            if (!this.$el) {
                return;
            }
            const options = this.$el.querySelectorAll('.segmented-option');
            const option = options[this.selectedIndex];
            if (!option) {
                return;
            }
            const parent = this.$el.getBoundingClientRect();
            const rect = option.getBoundingClientRect();
            if (!rect.width) {
                return;
            }
            this.thumbLeft = rect.left - parent.left;
            this.thumbWidth = rect.width;
            if (this.thumbReady) {
                return;
            }
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.thumbReady = true;
                });
            });
        },

    },

}

</script>
